import { useEffect, useState, type FormEvent } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { User, LogOut, Package, Bookmark, Loader2, ExternalLink, ShieldCheck } from 'lucide-react'
import toast from 'react-hot-toast'
import clsx from 'clsx'
import { useAuthStore } from '../context/store'
import { useSeo } from '../hooks/useSeo'
import { customerApi, ApiError, type CustomerOrder } from '../utils/customer'

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  return 'Something went wrong. Please try again.'
}

// ─── Sign-in ──────────────────────────────────────────────────────────────────

function SignIn() {
  const { login } = useAuthStore()
  const location = useLocation()
  const [params] = useSearchParams()
  const error = params.get('error')
  const returnTo = (location.state as { from?: string } | null)?.from || '/account'

  return (
    <div className="min-h-screen py-10 px-4">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-brand-gold/15 flex items-center justify-center mx-auto mb-4">
            <User size={26} className="text-brand-gold" />
          </div>
          <h1 className="font-serif text-3xl text-white">Your Account</h1>
          <p className="text-brand-gray text-sm mt-2">
            Track orders and keep your saved designs in one place.
          </p>
        </div>

        <div className="card p-6 space-y-4">
          {error && (
            <p className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{error}</p>
          )}
          <button onClick={() => login(returnTo)} className="btn-primary w-full min-h-[48px]">
            Sign in or create account
          </button>
          <p className="text-brand-gray text-xs flex items-start gap-2">
            <ShieldCheck size={14} className="mt-0.5 flex-shrink-0 text-brand-gold" />
            <span>
              No password needed. You'll receive a one-time code by email on Shopify's secure sign-in page, then return here.
            </span>
          </p>
        </div>
      </div>
    </div>
  )
}

// ─── Orders ───────────────────────────────────────────────────────────────────

function statusLabel(s: string | null) {
  if (!s) return '—'
  return s.toLowerCase().replace(/_/g, ' ')
}

function OrdersTab() {
  const [orders, setOrders] = useState<CustomerOrder[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    customerApi.orders()
      .then(r => setOrders(r.orders))
      .catch(err => setError(errorMessage(err)))
  }, [])

  if (error) return <p className="text-red-400 text-sm py-8">{error}</p>
  if (!orders) return <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-brand-gold" /></div>
  if (orders.length === 0) return (
    <div className="text-center py-16 text-brand-gray">
      <Package size={48} className="mx-auto mb-4 opacity-20" />
      <p>No orders yet.</p>
      <Link to="/catalogue" className="text-brand-gold hover:underline text-sm mt-2 inline-block">Browse the catalogue</Link>
    </div>
  )

  return (
    <div className="space-y-4">
      {orders.map(o => (
        <div key={o.id} className="card p-5">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <div>
              <p className="font-semibold text-white">Order {o.name}</p>
              <p className="text-brand-gray text-xs">{new Date(o.processedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</p>
            </div>
            <div className="text-right">
              <p className="text-white font-semibold">{Number(o.total.amount).toFixed(2)} {o.total.currencyCode}</p>
              <p className="text-brand-gray text-xs capitalize">{statusLabel(o.financialStatus)} · {statusLabel(o.fulfillmentStatus)}</p>
            </div>
          </div>
          <ul className="divide-y divide-brand-border">
            {o.lineItems.map((li, i) => (
              <li key={i} className="py-2.5 flex items-center gap-3 text-sm">
                {li.image ? (
                  <img src={li.image} alt="" className="w-12 h-12 rounded-lg object-cover border border-brand-border" />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-brand-navy border border-brand-border" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-white truncate">{li.title}</p>
                  {li.variantTitle && <p className="text-brand-gray text-xs">{li.variantTitle}</p>}
                </div>
                <span className="text-brand-gray">×{li.quantity}</span>
              </li>
            ))}
          </ul>
          {o.statusUrl && (
            <a href={o.statusUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-brand-gold hover:underline mt-3">
              Track order <ExternalLink size={14} />
            </a>
          )}
        </div>
      ))}
    </div>
  )
}

// ─── Profile ──────────────────────────────────────────────────────────────────

function ProfileTab() {
  const { customer, setCustomer } = useAuthStore()
  const [firstName, setFirstName] = useState(customer?.firstName || '')
  const [lastName, setLastName] = useState(customer?.lastName || '')
  const [saving, setSaving] = useState(false)

  const save = async (e: FormEvent) => {
    e.preventDefault()
    setSaving(true)
    try {
      const { customer: updated } = await customerApi.updateProfile({ firstName, lastName })
      setCustomer(updated)
      toast.success('Profile updated')
    } catch (err) {
      toast.error(errorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={save} className="card p-6 max-w-md space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label" htmlFor="pFirst">First name</label>
          <input id="pFirst" className="input" value={firstName} onChange={e => setFirstName(e.target.value)} />
        </div>
        <div>
          <label className="label" htmlFor="pLast">Last name</label>
          <input id="pLast" className="input" value={lastName} onChange={e => setLastName(e.target.value)} />
        </div>
      </div>
      <div>
        <label className="label">Email</label>
        <input className="input opacity-60" value={customer?.email || ''} readOnly />
        <p className="text-brand-gray text-xs mt-1">Email, phone and marketing preferences are managed on your Shopify account page.</p>
      </div>
      <button type="submit" disabled={saving} className="btn-primary min-h-[44px] flex items-center gap-2">
        {saving && <Loader2 size={16} className="animate-spin" />} Save Changes
      </button>
    </form>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function AccountPage() {
  useSeo({ title: 'My Account', path: '/account', noindex: true })
  const { isAuthenticated, initialized, customer, logout } = useAuthStore()
  const [tab, setTab] = useState<'orders' | 'profile'>('orders')

  if (!initialized) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="animate-spin text-brand-gold" size={32} />
    </div>
  )

  if (!isAuthenticated || !customer) return <SignIn />

  const name = [customer.firstName, customer.lastName].filter(Boolean).join(' ') || customer.displayName || customer.email

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-serif text-3xl text-white">{name}</h1>
            <p className="text-brand-gray">{customer.email}</p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/account/saved-designs" className="btn-secondary flex items-center gap-2 px-4 py-2 text-sm min-h-[44px]">
              <Bookmark size={16} /> Saved Designs
            </Link>
            <button onClick={logout} className="btn-ghost flex items-center gap-2 px-4 py-2 text-sm min-h-[44px]">
              <LogOut size={16} /> Sign Out
            </button>
          </div>
        </div>

        <div className="flex gap-4 mb-6 border-b border-brand-border">
          {(['orders', 'profile'] as const).map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={clsx(
                'pb-3 px-1 text-sm font-medium border-b-2 transition-colors capitalize min-h-[44px]',
                tab === t ? 'border-brand-gold text-brand-gold' : 'border-transparent text-brand-gray hover:text-white'
              )}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === 'orders' && <OrdersTab />}
        {tab === 'profile' && <ProfileTab />}
      </div>
    </div>
  )
}
